import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setStatus("timeout"); return; }
      attempts++;
      try {
        const res = await api.get(`/payment/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setStatus("success"); return; }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full border-border/50 shadow-sm">
        <CardContent className="pt-8 pb-6 text-center">
          <SpurwechselLogo size="default" className="justify-center mx-auto mb-4" />
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold mb-2">Zahlung wird verarbeitet...</h2>
              <p className="font-body text-sm text-muted-foreground">Bitte warten Sie einen Moment.</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold mb-2">Zahlung erfolgreich!</h2>
              <p className="font-body text-sm text-muted-foreground mb-6">Ihr Plan wurde aktiviert. Viel Erfolg bei Ihrer Vorbereitung!</p>
              <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-btn">
                Zum Dashboard
              </Button>
            </>
          )}
          {(status === "error" || status === "expired" || status === "timeout") && (
            <>
              <XCircle className="w-16 h-16 text-error mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold mb-2">
                {status === "expired" ? "Sitzung abgelaufen" : "Fehler bei der Zahlung"}
              </h2>
              <p className="font-body text-sm text-muted-foreground mb-6">Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.</p>
              <Button variant="outline" className="font-body" onClick={() => navigate("/pricing")} data-testid="try-again-btn">
                Erneut versuchen
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
